/**
 * @file allDayCalculator.service.ts
 * @description Computes All-Day aggregate preparation counts across all active kitchen orders.
 */

import type { KitchenOrder } from '../../../types/order.types';

export interface AllDayItemCount {
  itemId: string;
  name: string;
  station: string;
  totalQuantity: number;
  orderCount: number;
}

/**
 * Calculates aggregated item counts needed across all open orders.
 */
export const calculateAllDayCounts = (
  orders: KitchenOrder[],
  selectedStation: string = 'all'
): AllDayItemCount[] => {
  const map = new Map<string, AllDayItemCount>();

  for (const order of orders) {
    // Skip orders and items that are already plated at pass or cancelled
    if (order.status === 'ready' || order.status === 'cancelled') continue;

    for (const item of order.items) {
      if (item.status === 'cancelled' || item.status === 'ready') continue;

      const station = item.kitchen_station || 'hot';
      // When viewing all kitchen stations, omit drinks since beverages are handled by waitstaff and the bar
      if (selectedStation === 'all' && station.toLowerCase() === 'drinks') {
        continue;
      }
      if (selectedStation !== 'all' && station.toLowerCase() !== selectedStation.toLowerCase()) {
        continue;
      }

      const existing = map.get(item.item_id);
      if (existing) {
        existing.totalQuantity += item.quantity;
        existing.orderCount += 1;
      } else {
        map.set(item.item_id, {
          itemId: item.item_id,
          name: item.name,
          station,
          totalQuantity: item.quantity,
          orderCount: 1,
        });
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
};
